import Layout from "@/components/Layout";
import AnimatedImage from "@/components/AnimatedImage";
import ProductInfoPanel from "@/components/ProductInfoPanel";
import Seo from "@/components/Seo";
import { useReveal } from "@/hooks/useReveal";

import immunsupport from "@/assets/immunsupport.webp";
import cVitamin from "@/assets/c-vitamin.webp";
import solGladje from "@/assets/sol-gladje.webp";
import elektrolyter from "@/assets/elektrolyter.webp";
import immunFrukt from "@/assets/immunsupport-frukt.webp";
import cFrukt from "@/assets/cvitamin-frukt.webp";
import solFrukt from "@/assets/solgladje-frukt.webp";
import elekFrukt from "@/assets/elektrolyter-frukt.webp";
import immunGlas from "@/assets/immunsupport-glas.webp";
import cGlas from "@/assets/cvitamin-glas.webp";
import solGlas from "@/assets/solgladje-glas.webp";
import elekGlas from "@/assets/elektrolyter-glas.webp";
import immunLifestyle from "@/assets/immunsupport-lifestyle.webp";
import cLifestyle from "@/assets/cvitamin-lifestyle.webp";
import solLifestyle from "@/assets/solgladje-lifestyle.webp";
import elekLifestyle from "@/assets/elektrolyter-lifestyle.webp";
import tropiTube from "@/assets/tropikollagen.webp";
import tropiFrukt from "@/assets/tropikollagen-frukt.webp";
import tropiGlas from "@/assets/tropikollagen-glas.webp";
import tropiLifestyle from "@/assets/tropikollagen-lifestyle.webp";

interface Product {
  id: string;
  name: string;
  flavor: string;
  description: string;
  image: string;
  fruitImage: string;
  glassImage: string;
  lifestyleImage: string;
  accentColor: string;
}

const products: Product[] = [
  {
    id: "immunsupport",
    name: "Immunsupport",
    flavor: "Med smak av hallon och jordgubb",
    description: "Framtagen för att ge kroppen extra stöd när det behövs som mest. Innehåller C-vitamin, zink och svart fläder, som tillsammans bidrar till ett starkt immunförsvar och en tryggare vardag.",
    image: immunsupport,
    fruitImage: immunFrukt,
    glassImage: immunGlas,
    lifestyleImage: immunLifestyle,
    accentColor: "border-purple-500",
  },
  {
    id: "c-vitamin",
    name: "C-Vitamin",
    flavor: "Med smak av apelsin",
    description: "Hög dos C-vitamin med naturlig apelsinsmak. Bidrar till normal funktion av immunsystemet och skyddar cellerna mot oxidativ stress.",
    image: cVitamin,
    fruitImage: cFrukt,
    glassImage: cGlas,
    lifestyleImage: cLifestyle,
    accentColor: "border-orange-500",
  },
  {
    id: "sol-gladje",
    name: "Sol & Glädje",
    flavor: "Med smak av citrus",
    description: "Vitamin D och B-vitaminer för energi och välmående. Fräsch citrussmak som lyfter dagen och ger kroppen det den behöver.",
    image: solGladje,
    fruitImage: solFrukt,
    glassImage: solGlas,
    lifestyleImage: solLifestyle,
    accentColor: "border-yellow-500",
  },
  {
    id: "elektrolyter",
    name: "Elektrolyter",
    flavor: "Med smak av lemonad",
    description: "Mineraler och elektrolyter för hydrering och återhämtning. Läskande lemonadsmak som gör det enkelt att fylla på under dagen.",
    image: elektrolyter,
    fruitImage: elekFrukt,
    glassImage: elekGlas,
    lifestyleImage: elekLifestyle,
    accentColor: "border-green-500",
  },
  {
    id: "tropikollagen",
    name: "Tropikollagen",
    flavor: "Med smak av mango",
    description: "Marint kollagen och hyaluronsyra i en frisk mangodryck. Med C-vitamin, B-vitaminer och biotin som bidrar till hud, hår och normal energiomsättning.",
    image: tropiTube,
    fruitImage: tropiFrukt,
    glassImage: tropiGlas,
    lifestyleImage: tropiLifestyle,
    accentColor: "border-pink-500",
  },
];

const ProdukterPage = () => {
  const productLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: products.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        name: p.name,
        description: p.description,
        brand: { "@type": "Brand", name: "aQuadias" },
      },
    })),
  };
  return (
    <Layout>
      <Seo
        title="Produkter – Aquadias brustabletter med vitaminer"
        description="Fem brustabletter med naturliga smaker: Immunsupport, C-Vitamin, Sol & Glädje, Elektrolyter och Tropikollagen. Vitaminer och mineraler för en friskare arbetsdag."
        path="/produkter"
        jsonLd={productLd}
      />
      <section className="bg-gradient-to-b from-coral-500/20 via-cream-100 to-cream-50 min-h-[70vh] flex flex-col justify-end px-6 md:px-12 pb-12">
        <h1 className="text-6xl md:text-8xl lg:text-9xl font-black uppercase tracking-tighter text-foreground mb-4 leading-[0.9]">
          Våra<br />Produkter
        </h1>
        <p className="text-muted-foreground max-w-lg text-base md:text-lg">
          Brustabletter med naturliga smaker och ingredienser. Fem unika formler, en för varje behov.
        </p>
      </section>

      <section className="py-20 md:py-28 bg-cream-50">
        <div className="space-y-20 md:space-y-28">
          {products.map((product, index) => {
            const isReversed = index % 2 !== 0;
            return (
              <ProductCard key={product.name} product={product} isReversed={isReversed} />
            );
          })}
        </div>
      </section>
    </Layout>
  );
};

interface ProductCardProps {
  product: Product;
  isReversed: boolean;
}

const ProductCard = ({ product, isReversed }: ProductCardProps) => {
  const textReveal = useReveal({ fromRight: isReversed, threshold: 0.2, rootMargin: "30px" });

  return (
    <div className="grid md:grid-cols-2 gap-8 md:gap-0 items-center px-6 md:px-12">
      <div
        ref={textReveal.ref}
        className={`space-y-5 max-w-md ${isReversed ? "md:order-last md:pl-12" : ""} ${textReveal.className}`}
      >
        <h2 className={`text-3xl md:text-4xl font-bold text-foreground border-l-4 ${product.accentColor} pl-4`}>
          {product.name}
        </h2>
        <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
          {product.description}
        </p>
        <p className="text-sm font-medium text-foreground">
          {product.flavor}
        </p>

        <ProductInfoPanel productId={product.id} />
      </div>

      <div className={`grid grid-cols-3 grid-rows-2 gap-2 md:gap-3 h-[280px] md:h-[360px] ${isReversed ? "md:order-first" : ""}`}>
        <div className="row-span-2">
          <AnimatedImage src={product.image} alt={product.name} delay={0} fill />
        </div>
        <div>
          <AnimatedImage src={product.glassImage} alt={`${product.name} i glas`} delay={100} fill />
        </div>
        <div>
          <AnimatedImage src={product.lifestyleImage} alt={`${product.name} lifestyle`} delay={150} fill />
        </div>
        <div className="col-span-2">
          <AnimatedImage src={product.fruitImage} alt={`${product.name} ingredienser`} delay={200} fill />
        </div>
      </div>
    </div>
  );
};

export default ProdukterPage;
